/**
 * ============================================================================
 * NewCapaDialog — modal dialog for creating a new CAPA (Phase 7).
 * Owner: Phase 7 — CAPA Module agent.
 * URS rows: CAPA-001, CAPA-003, CAPA-004.
 * ============================================================================ */

import { useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './CapaWorkbench.module.css';
import type {
  CapaCriticality,
  CapaSourceKind,
  CapaType,
} from './useCapasQuery';

export interface NewCapaDialogProps {
  onClose: () => void;
  onCreated: (capaId: string) => void;
}

interface FormState {
  title: string;
  description: string;
  capaType: CapaType;
  sourceKind: CapaSourceKind;
  sourceRef: string;
  ownerUserId: string;
  criticality: CapaCriticality;
}

const INITIAL: FormState = {
  title: '',
  description: '',
  capaType: 'CORRECTIVE',
  sourceKind: 'OTHER',
  sourceRef: '',
  ownerUserId: '',
  criticality: 'MAJOR',
};

export function NewCapaDialog({ onClose, onCreated }: NewCapaDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  function update<K extends keyof FormState>(key: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [key]: v }));
  }

  async function submit() {
    if (!form.title.trim() || !form.description.trim() || !form.ownerUserId.trim()) {
      setErrorMsg(
        t('capa.create.missingRequired', {
          defaultValue: 'Title, description, and owner are required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(apiUrl('/api/v1/capa'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim(),
          capaType: form.capaType,
          sourceKind: form.sourceKind,
          sourceRef: form.sourceRef.trim() || null,
          ownerUserId: form.ownerUserId.trim(),
          criticality: form.criticality,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('capa.create.failed', {
              defaultValue: 'Create CAPA failed.',
            }),
        );
        return;
      }
      const body = (await r.json()) as { capaId: string };
      onCreated(body.capaId);
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.sectionTitle}>
          {t('capa.create.title', { defaultValue: 'New CAPA' })}
        </h2>
        <div className={styles.formField}>
          <label htmlFor="capa-title">
            {t('capa.create.titleLabel', { defaultValue: 'Title' })}
          </label>
          <input
            id="capa-title"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
          />
        </div>
        <div className={styles.formField}>
          <label htmlFor="capa-desc">
            {t('capa.create.descLabel', { defaultValue: 'Description' })}
          </label>
          <textarea
            id="capa-desc"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
          />
        </div>
        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="capa-type">
              {t('capa.create.typeLabel', { defaultValue: 'Type' })}
            </label>
            <select
              id="capa-type"
              value={form.capaType}
              onChange={(e) => update('capaType', e.target.value as CapaType)}
            >
              <option value="CORRECTIVE">CORRECTIVE</option>
              <option value="PREVENTIVE">PREVENTIVE</option>
              <option value="BOTH">BOTH</option>
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="capa-crit">
              {t('capa.create.critLabel', { defaultValue: 'Criticality' })}
            </label>
            <select
              id="capa-crit"
              value={form.criticality}
              onChange={(e) =>
                update('criticality', e.target.value as CapaCriticality)
              }
            >
              <option value="CRITICAL">CRITICAL</option>
              <option value="MAJOR">MAJOR</option>
              <option value="MINOR">MINOR</option>
            </select>
          </div>
        </div>
        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="capa-source">
              {t('capa.create.sourceLabel', { defaultValue: 'Source' })}
            </label>
            <select
              id="capa-source"
              value={form.sourceKind}
              onChange={(e) =>
                update('sourceKind', e.target.value as CapaSourceKind)
              }
            >
              <option value="DEVIATION">DEVIATION</option>
              <option value="OOS">OOS</option>
              <option value="AUDIT_FINDING">AUDIT_FINDING</option>
              <option value="RISK">RISK</option>
              <option value="COMPLAINT">COMPLAINT</option>
              <option value="MGMT_REVIEW">MGMT_REVIEW</option>
              <option value="CC_OUTCOME">CC_OUTCOME</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="capa-srcref">
              {t('capa.create.sourceRefLabel', {
                defaultValue: 'Source reference',
              })}
            </label>
            <input
              id="capa-srcref"
              value={form.sourceRef}
              onChange={(e) => update('sourceRef', e.target.value)}
            />
          </div>
        </div>
        <div className={styles.formField}>
          <label htmlFor="capa-owner">
            {t('capa.create.ownerLabel', { defaultValue: 'Owner user id' })}
          </label>
          <input
            id="capa-owner"
            value={form.ownerUserId}
            onChange={(e) => update('ownerUserId', e.target.value)}
          />
        </div>
        {errorMsg && (
          <div className={styles.errorBanner} role="alert">
            {errorMsg}
          </div>
        )}
        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('capa.create.submit', { defaultValue: 'Create CAPA' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
